import mapboxgl from 'mapbox-gl';
import { formatCoordinates } from '../../../utils/coordinateFormatters';
import { logger } from '../../../utils/logger';

const log = logger('PlacenameHandler');

interface PlacenameItem {
  name: string;
  latitude: number;
  longitude: number;
  distance?: number;
  concise?: { term: string };
  province?: { description: string };
}

/**
 * PlacenameHandler - manages placename lookup and search operations.
 * Always alive in EasyMap, independent of PlacenameTool UI component.
 */
export class PlacenameHandler {
  private map: mapboxgl.Map;
  private markers: mapboxgl.Marker[] = [];
  private searchPopup: mapboxgl.Popup | null = null;
  private clearControl: HTMLElement | null = null;

  constructor(map: mapboxgl.Map) {
    this.map = map;
  }

  /**
   * Lookup placenames within 5km of the given location
   */
  public async lookup(lngLat: mapboxgl.LngLat): Promise<void> {
    const { lat, lng: lon } = lngLat;
    log.debug('lookup at', lat, lon);

    const loadingPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat(lngLat)
      .setHTML('<div class="placename-loading"><i class="fa fa-spinner fa-spin"></i> Searching nearby...</div>')
      .addTo(this.map);

    try {
      const response = await fetch('/api/placename/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lat, lon, radius: 5 }),
      });

      const data = await response.json();
      loadingPopup.remove();

      if (data.success && data.items.length > 0) {
        this.showResultsOnMap(data.items);
      } else {
        this.showMessagePopup(lngLat, 'No places found nearby');
      }
    } catch (error) {
      log.error('Placename lookup error:', error);
      loadingPopup.remove();
      this.showMessagePopup(lngLat, 'Failed to lookup placenames');
    }
  }

  /**
   * Show search form popup at the given location
   */
  public showSearchForm(lngLat: mapboxgl.LngLat): void {
    const { lat, lng: lon } = lngLat;
    log.debug('showSearchForm at', lat, lon);

    if (this.searchPopup) {
      this.searchPopup.remove();
    }

    const container = document.createElement('div');
    container.className = 'placename-search-form';
    container.innerHTML = `
      <div class="search-header">Search this Province/Territory</div>
      <input type="text" id="placename-search-input" placeholder="Enter place name..." />
      <button id="placename-search-btn">Search</button>
    `;

    const popup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat(lngLat)
      .setDOMContent(container)
      .addTo(this.map);

    this.searchPopup = popup;

    setTimeout(() => {
      const input = document.getElementById('placename-search-input') as HTMLInputElement;
      const btn = document.getElementById('placename-search-btn');

      if (input && btn) {
        input.focus();

        const doSearch = async () => {
          const query = input.value.trim();
          if (!query) return;
          popup.remove();
          await this.performSearch(lat, lon, query);
        };

        btn.addEventListener('click', doSearch);
        input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') doSearch();
        });
      }
    }, 100);
  }

  public destroy(): void {
    this.clearMarkers();
    this.removeClearControl();
    if (this.searchPopup) {
      this.searchPopup.remove();
      this.searchPopup = null;
    }
  }

  private async performSearch(lat: number, lon: number, query: string): Promise<void> {
    const lngLat = new mapboxgl.LngLat(lon, lat);

    const loadingPopup = new mapboxgl.Popup({ closeButton: true, closeOnClick: false })
      .setLngLat(lngLat)
      .setHTML(`<div class="placename-loading"><i class="fa fa-spinner fa-spin"></i> Searching for "${query}"...</div>`)
      .addTo(this.map);

    try {
      const response = await fetch('/api/placename/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lat, lon, query }),
      });

      const data = await response.json();
      loadingPopup.remove();

      if (data.success && data.items.length > 0) {
        this.showResultsOnMap(data.items);
      } else if (data.error === 'Location is not within Canada') {
        this.showMessagePopup(lngLat, 'Location is not within Canada');
      } else {
        this.showMessagePopup(lngLat, `No places found for "${query}"`);
      }
    } catch (error) {
      log.error('Placename search error:', error);
      loadingPopup.remove();
      this.showMessagePopup(lngLat, 'Failed to search placenames');
    }
  }

  private clearMarkers(): void {
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
    this.removeClearControl();
  }

  private showClearControl(): void {
    this.removeClearControl();

    const btn = document.createElement('button');
    btn.className = 'placename-clear-control';
    btn.innerHTML = '&#x2716; Clear Placenames';
    btn.title = 'Remove all placename markers from the map';
    btn.addEventListener('click', () => this.clearMarkers());

    this.map.getContainer().appendChild(btn);
    this.clearControl = btn;
  }

  private removeClearControl(): void {
    if (this.clearControl) {
      this.clearControl.remove();
      this.clearControl = null;
    }
  }

  private showMessagePopup(lngLat: mapboxgl.LngLat, message: string): void {
    new mapboxgl.Popup({ closeButton: true, closeOnClick: true })
      .setLngLat(lngLat)
      .setHTML(`<div class="placename-no-results">${message}</div>`)
      .addTo(this.map);
  }

  private showResultsOnMap(items: PlacenameItem[]): void {
    this.clearMarkers();

    const displayItems = items.slice(0, 50);
    const bounds = new mapboxgl.LngLatBounds();

    displayItems.forEach((item) => {
      const coords = formatCoordinates(item.latitude, item.longitude);

      const el = document.createElement('div');
      el.className = 'placename-marker';
      el.innerHTML = `
        <div class="placename-marker-pin"></div>
        <div class="placename-marker-label">
          <span class="marker-name">${item.name}</span>
          <span class="marker-type">${item.concise?.term || ''}</span>
        </div>
      `;

      const popup = new mapboxgl.Popup({ offset: 25, closeButton: true })
        .setHTML(`
          <div class="placename-marker-popup">
            <strong>${item.name}</strong><br/>
            <span class="popup-type">${item.concise?.term || ''}</span>
            ${item.distance ? `<br/><span class="popup-distance">${item.distance.toFixed(1)} km away</span>` : ''}
            <div class="popup-coords">
              <div class="coords-label">DDM:</div>
              <div class="coords-value">${coords.dmm}</div>
              <div class="coords-label">DD:</div>
              <div class="coords-value">${coords.dd}</div>
              <div class="coords-label">DMS:</div>
              <div class="coords-value">${coords.dms}</div>
            </div>
          </div>
        `);

      const marker = new mapboxgl.Marker({ element: el })
        .setLngLat([item.longitude, item.latitude])
        .setPopup(popup)
        .addTo(this.map);

      el.addEventListener('click', () => {
        this.map.flyTo({
          center: [item.longitude, item.latitude],
          zoom: 14,
          duration: 1000
        });
      });

      this.markers.push(marker);
      bounds.extend([item.longitude, item.latitude]);
    });

    if (displayItems.length > 0) {
      this.map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 12
      });
      this.showClearControl();
    }
  }
}
